import React from 'react';
import { FiExternalLink, FiGithub, FiLock } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Projects = () => {
    const { t } = useTranslation();

    const projects = [
        {
            titleKey: 'itinerary',
            screenshot: '/projects/itinerary.png',
            tech: ['React', 'CRA', 'Tailwind CSS', 'Leaflet', 'Framer Motion', 'Cloudflare Pages'],
            features: ['Interactive maps', 'Password-protected', 'Shared notes', 'Preview mode'],
            liveUrl: 'https://itinerary.rontaro.com',
            featured: true,
        },
        {
            titleKey: 'finance',
            screenshot: '/projects/finance.png',
            tech: ['React', 'Vite', 'Recharts', 'Cloudflare Workers', 'D1', 'KV', 'Yahoo Finance', 'Gemini'],
            features: ['ETF portfolio', 'Budget tracking', 'Analytics', 'Net worth', 'Recurring & loans', 'Trips', 'Telegram bot + OCR', 'Demo mode'],
            liveUrl: 'https://budget.rontaro.com',
            featured: true,
        },
        {
            titleKey: 'tracker',
            screenshot: '/projects/tracker.png',
            tech: ['React', 'Vite', 'Tailwind CSS', 'Cloudflare Workers', 'D1', 'KV'],
            features: ['Habits heatmap', 'Media backlog', 'MFC collection', 'AniList import', 'RSS/OPML feeds', 'Link inbox', 'Telegram bot', 'Demo mode'],
            liveUrl: 'https://tracker.rontaro.com',
            featured: true,
        },
        {
            titleKey: 'diet',
            screenshot: '/projects/diet.png',
            tech: ['React', 'Vite', 'Tailwind CSS', 'Cloudflare Workers', 'D1', 'R2', 'MCP'],
            features: ['Meal plan', 'Macro tracking', 'Photo log', 'Telegram reminders', 'Claude via MCP', 'Demo mode'],
            liveUrl: 'https://diet.rontaro.com',
            featured: true,
        },
        {
            titleKey: 'fanta',
            screenshot: '/projects/fanta.png',
            tech: ['React', 'Vite', 'Tailwind CSS v4', 'Cloudflare Workers', 'Durable Objects', 'WebSocket'],
            features: ['Real-time bidding', 'Buzzer pads', 'Remote players', 'Random draw', 'Listone import', 'Fantaleghe export'],
            liveUrl: 'https://fanta.rontaro.com',
            featured: true,
        },
        {
            titleKey: 'poker',
            screenshot: '/projects/poker.png',
            tech: ['TypeScript', 'pnpm monorepo', 'Web Worker', 'SQLite WASM · OPFS', 'React', 'Recharts', 'Vitest'],
            features: ['Hand history parser', 'Leak detection', 'Equity', 'Trainer', '100% in-browser', '577 tests'],
            liveUrl: 'https://poker.rontaro.com',
            repoUrl: 'https://github.com/Rontaro/Poker-toolkit',
            featured: true,
        },
        {
            titleKey: 'shortener',
            screenshot: '/projects/shortener.png',
            tech: ['React', 'Vite', 'Cloudflare Pages Functions', 'KV', 'Turnstile'],
            features: ['Edge redirects', 'Anti-bot', 'Rate limiting', 'Telegram alerts'],
            liveUrl: 'https://go.rontaro.com',
            featured: true,
        },
        {
            titleKey: 'waypoint',
            screenshot: '/projects/waypoint.png',
            tech: ['React', 'Vite', 'Cloudflare Workers', 'Google Maps API'],
            features: ['Route optimization', 'Walking time', 'Transit time'],
            liveUrl: 'https://waypoints.rontaro.com',
            featured: false,
        },
        {
            titleKey: 'leetcode',
            screenshot: '/projects/leetcode.png',
            tech: ['React', 'Tailwind CSS', 'React Router', 'Cloudflare Pages'],
            features: ['40+ solutions', 'Syntax highlight', 'Difficulty filters', 'Topic tags'],
            liveUrl: 'https://leetcode.rontaro.com',
            featured: false,
        },
    ];

    const internalTools = [
        {
            titleKey: 'workers',
            tech: ['Hono v4', 'Cloudflare Workers', 'D1 SQLite', 'KV', 'R2'],
            features: ['REST API', 'JWT auth', 'Nightly backup', 'Telegram bots', 'OpenAPI docs'],
        },
        {
            titleKey: 'admin',
            tech: ['React', 'Vite', 'Tailwind CSS', 'Cloudflare Pages'],
            features: ['Trip editor', 'User management', 'Link management', 'Finance admin'],
        },
    ];

    return (
        <section id="projects" className="relative section">
            {/* Background orbs for continuity */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div 
                    animate={{ x: [0, -50, 0], y: [0, 60, 0] }} 
                    transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-1/3 -left-20 w-96 h-96 bg-blue-200/20 dark:bg-blue-900/10 rounded-full blur-[120px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Section Title */}
                    <div className="mb-12">
                        <span className="section-number">03 — Projects</span>
                        <h2 className="section-title mt-2">{t('projects.sectionTitle')}</h2>
                    </div>

                    {/* Projects Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {projects.map((project, index) => (
                            <motion.div
                                key={index}
                                className="card flex flex-col group bg-white/40 dark:bg-zinc-900/30 border-white/50 dark:border-zinc-800/50 backdrop-blur-xl"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                {/* Screenshot Container */}
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={project.screenshot}
                                        alt={t(`projects.items.${project.titleKey}.title`)}
                                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
                                    
                                    {project.featured && (
                                        <div className="absolute top-4 right-4 z-10">
                                            <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-amber-500/20">
                                                {t('projects.featured')}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="p-6 flex flex-col flex-1">
                                    <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50 mb-3 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                                        {t(`projects.items.${project.titleKey}.title`)}
                                    </h3>

                                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
                                        {t(`projects.items.${project.titleKey}.description`)}
                                    </p>

                                    {/* Features Tags */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {project.features.map((feature, i) => (
                                            <span key={i} className="font-mono text-[10px] px-2 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400 border border-zinc-200/50 dark:border-zinc-700/50">
                                                {feature}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Tech Tags */}
                                    <div className="flex flex-wrap gap-2 mb-8">
                                        {project.tech.map((tech, i) => (
                                            <span key={i} className="tag whitespace-nowrap">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-6 mt-auto">
                                        {project.liveUrl && (
                                            <a
                                                href={project.liveUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 text-sm font-bold text-amber-600 dark:text-amber-400 group/link"
                                            >
                                                <span className="relative">
                                                    {t('projects.liveDemo')}
                                                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-600 dark:bg-amber-400 transition-all group-hover/link:w-full" />
                                                </span>
                                                <FiExternalLink className="transition-transform group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                                            </a>
                                        )}
                                        {project.repoUrl && (
                                            <a
                                                href={project.repoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 text-sm font-bold text-zinc-500 dark:text-zinc-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                                            >
                                                <FiGithub />
                                                {t('projects.code')}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        {/* Coming Soon Card */}
                        <motion.div
                            className="card flex flex-col items-center justify-center p-8 bg-zinc-50/20 dark:bg-zinc-900/10 border-2 border-dashed border-zinc-200 dark:border-zinc-800/30 opacity-60 grayscale hover:grayscale-0 transition-all duration-700 min-h-[400px]"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 0.6, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: projects.length * 0.1 }}
                        >
                            <div className="text-center group-hover:scale-105 transition-transform duration-500">
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 dark:text-zinc-600 block mb-4">
                                    {String(projects.length + 1).padStart(2, '0')}
                                </span>
                                <h3 className="text-zinc-900 dark:text-zinc-50 font-bold text-lg mb-2">
                                    {t('projects.moreProjects')}
                                </h3>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                    {t('projects.comingSoon')}
                                </p>
                            </div>
                        </motion.div>

                    </div>

                    {/* Internal Tools Section */}
                    <motion.div
                        className="relative pt-16 border-t border-zinc-200 dark:border-zinc-800"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="flex items-center gap-4 mb-10">
                            <span className="section-number mb-0">{t('projects.internalTools')}</span>
                            <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {internalTools.map((tool, index) => (
                                <motion.div 
                                    key={index} 
                                    whileHover={{ y: -5 }}
                                    className="card p-8 bg-zinc-50/50 dark:bg-zinc-900/20"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">
                                            {t(`projects.items.${tool.titleKey}.title`)}
                                        </h3>
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800/50 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700/50 text-[10px] uppercase font-bold tracking-widest">
                                            <FiLock size={12} />
                                            {t('projects.private')}
                                        </span>
                                    </div>
                                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
                                        {t(`projects.items.${tool.titleKey}.description`)}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {tool.features.map((feature, i) => (
                                            <span key={i} className="font-mono text-[10px] px-2 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400 border border-zinc-200/50 dark:border-zinc-700/50">
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {tool.tech.map((tech, i) => (
                                            <span key={i} className="tag">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default Projects;
